import React, { useCallback, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Tooltip, message } from 'antd'
import API from 'utils/api'
import { useToken } from 'app/hooks'
import useUserDevices, { DeviceServiceKey, UserDevice } from 'app/gomsacademy/hooks/UserDevices'
import {
	Card,
	EmptyState,
	Footer,
	Header,
	PageWrapper,
	StatusBadge,
	TableCard,
	TableWrapper,
} from '../ServiceManagement/styles'
import { ActivityText, DeviceTable, Filters, ServiceChip, SessionActions, SubText } from './styles'

type StatusFilter = 'all' | 'active' | 'inactive'
type ServiceFilter = 'all' | DeviceServiceKey

export const DeviceManagement: React.FC = () => {
	const { data: token } = useToken()
	const { devices, isLoading, isValidating, error, mutate } = useUserDevices()

	const [serviceFilter, setServiceFilter] = useState<ServiceFilter>('all')
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
	const [revokingSession, setRevokingSession] = useState<string | null>(null)

	const filteredDevices = useMemo(() => {
		return devices
			.filter(device => {
				if (serviceFilter !== 'all' && device.serviceKey !== serviceFilter) {
					return false
				}
				if (statusFilter === 'active' && !device.isActive) {
					return false
				}
				if (statusFilter === 'inactive' && device.isActive) {
					return false
				}
				return true
			})
			.sort((a, b) => {
				const aTime = a.lastActivity || a.loginAt || ''
				const bTime = b.lastActivity || b.loginAt || ''
				if (!aTime && !bTime) return 0
				if (!aTime) return 1
				if (!bTime) return -1
				return dayjs(bTime).valueOf() - dayjs(aTime).valueOf()
			})
	}, [devices, serviceFilter, statusFilter])

	const activeSessions = useMemo(() => devices.filter(device => device.isActive).length, [devices])

	const formatDate = useCallback((value: string | null) => {
		if (!value) return '-'
		const parsed = dayjs(value)
		if (!parsed.isValid()) return '-'
		return parsed.format('YYYY.MM.DD HH:mm')
	}, [])

	const handleResetFilters = useCallback(() => {
		setServiceFilter('all')
		setStatusFilter('all')
	}, [])

	const handleRefresh = useCallback(async () => {
		await mutate()
	}, [mutate])

	const handleRevokeSession = useCallback(
		async (device: UserDevice) => {
			if (!token) {
				message.error('인증 정보가 없습니다. 다시 로그인해 주세요.')
				return
			}

			if (!device.isActive) {
				message.info('이미 종료된 세션입니다.')
				return
			}

			const confirmed = window.confirm('선택한 세션을 즉시 종료하시겠습니까?')
			if (!confirmed) return

			setRevokingSession(device.sessionToken)
			try {
				const response = await API.delete(`/auth/devices/${device.sessionToken}`, token + '', {})
				const result = response?.result

				if (result?.success) {
					message.success(result?.message || '세션을 종료했습니다.')
				} else {
					message.warning(result?.message || '세션을 종료하지 못했습니다.')
				}
			} catch (err: any) {
				console.error(err)
				message.error(err?.message || '세션 종료에 실패했습니다.')
			} finally {
				setRevokingSession(null)
				await mutate()
			}
		},
		[mutate, token]
	)

	return (
		<PageWrapper>
			<Card>
				<Header>
					<div>
						<h2>접속기기 관리</h2>
						<p>현재 로그인된 기기와 세션을 확인하고 필요 시 즉시 종료할 수 있습니다.</p>
					</div>
					<span className="summary">
						총 세션: <strong>{devices.length}</strong> · 활성: <strong>{activeSessions}</strong>
						{isValidating && ' (동기화 중)'}
					</span>
				</Header>
				<Filters>
					<select value={serviceFilter} onChange={event => setServiceFilter(event.target.value as ServiceFilter)}>
						<option value="all">전체 서비스</option>
						<option value="goms">GOMS</option>
						<option value="want">WANT</option>
					</select>
					<select value={statusFilter} onChange={event => setStatusFilter(event.target.value as StatusFilter)}>
						<option value="all">전체 상태</option>
						<option value="active">활성 세션</option>
						<option value="inactive">종료된 세션</option>
					</select>
					<button type="button" onClick={handleResetFilters}>
						필터 초기화
					</button>
					<button type="button" onClick={handleRefresh} disabled={isLoading}>
						새로고침
					</button>
				</Filters>
				{error && (
					<StatusBadge $active={false}>접속기기 정보를 불러오지 못했습니다.</StatusBadge>
				)}
			</Card>

			<TableCard>
				<TableWrapper>
					{isLoading ? (
						<EmptyState>접속기기 정보를 불러오는 중입니다...</EmptyState>
					) : filteredDevices.length === 0 ? (
						<EmptyState>조건에 해당하는 기기 세션이 없습니다.</EmptyState>
					) : (
						<DeviceTable>
							<thead>
								<tr>
									<th>서비스</th>
									<th>기기 / 브라우저</th>
									<th>네트워크</th>
									<th>로그인</th>
									<th>최근 활동</th>
									<th>상태</th>
									<th>관리</th>
								</tr>
							</thead>
							<tbody>
								{filteredDevices.map(device => (
									<tr key={device.sessionToken}>
										<td>
											<ServiceChip $service={device.serviceKey}>{device.serviceKey}</ServiceChip>
										</td>
										<td>
											<ActivityText>{device.deviceName || '알 수 없는 기기'}</ActivityText>
											{device.userAgent ? (
												<Tooltip title={device.userAgent}>
													<SubText>{device.userAgent.slice(0, 40)}{device.userAgent.length > 40 ? '…' : ''}</SubText>
												</Tooltip>
											) : (
												<SubText>브라우저 정보 없음</SubText>
											)}
											<SubText className="token">{device.sessionToken}</SubText>
										</td>
										<td>
											<ActivityText>{device.ipAddress || 'IP 정보 없음'}</ActivityText>
											<SubText>Device ID: {device.deviceId || '미등록'}</SubText>
										</td>
										<td>{formatDate(device.loginAt)}</td>
										<td>{formatDate(device.lastActivity)}</td>
										<td>
											<StatusBadge $active={device.isActive}>
												{device.isActive ? '활성' : '종료됨'}
											</StatusBadge>
											{!device.isActive && device.revokedAt && (
												<SubText>종료: {formatDate(device.revokedAt)}</SubText>
											)}
										</td>
										<td>
											<SessionActions>
												<button
													type="button"
													onClick={() => handleRevokeSession(device)}
													disabled={
														!device.isActive || revokingSession === device.sessionToken || isLoading
													}>
													{revokingSession === device.sessionToken ? '종료 중...' : '세션 종료'}
												</button>
											</SessionActions>
										</td>
									</tr>
								))}
							</tbody>
						</DeviceTable>
					)}
				</TableWrapper>
				<Footer>
					<div className="page-info">
						총 {devices.length}개 세션 중 {activeSessions}개 활성화
					</div>
					<div>
						<small style={{ color: '#8a8a8a' }}>
							목록은 로그인 시간 기준 최근 순으로 정렬됩니다.
						</small>
					</div>
				</Footer>
			</TableCard>
		</PageWrapper>
	)
}

export default React.memo(DeviceManagement)
